import {
  LauncherElementData,
  TreeNode,
  useLauncherElementsStore,
} from '../../hooks/stores/launcherElementsStore';
import { LauncherStatesData, useLauncherStatesStore } from '../../hooks/stores/launcherStatesStore';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';
import { useMemo, useRef } from 'react';

import { convertObjectKeysFromCamelCaseToKebabCase } from '../../functions/utils/convertObjectKeysFromCamelCaseToKebabCase';
import { convertObjectValuesToStrings } from '../../functions/utils/convertObjectValuesToStrings';
import { launcherElementInfo } from '../../info/launcherElementInfo';
import { useElementCssClassName } from '../../hooks/useElementCssClassName';
import { useScene } from '../../context/sceneContext';

// Keep track of how many times each changelog field has been rendered
let changelogLabelCounter = 0;
let changelogDateCounter = 0;
let changelogContentCounter = 0;

export const LauncherElement = ({ data }: { data: TreeNode }) => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const [launcherStates] = useLauncherStatesStore<LauncherStatesData>();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const elementRef = useRef<HTMLElement>(null);
  const { type, props, name } = launcherElementsData[data.id] || {};
  const commands = props?.commands;

  const HTMLTagName = launcherElementInfo[type]?.HTMLTagName;

  const elementCssClassName = useElementCssClassName(data.id, props?.class);

  const launcherStatesProps = convertObjectValuesToStrings(
    convertObjectKeysFromCamelCaseToKebabCase(launcherStates)
  );

  const variablesDefault = launcherElementInfo[type]?.variablesDefault;
  const variable = props?.variable;
  let defaultVariableText =
    variable && variablesDefault ? variablesDefault[variable] : undefined;

  // TODO: add back changelog variable text functionality
  // Mock changelog variable text in simulation mode
  /*
  if (UIData.editorMode === 'simulation' && variable && variable.startsWith('appChangelogEntry')) {
    switch (variable) {
      case 'appChangelogEntryLabel':
        const versions = ['2.1.0', '2.0.5', '2.0.0', '1.9.8', '1.9.7'];
        defaultVariableText = versions[changelogLabelCounter % versions.length];
        changelogLabelCounter++;
        break;
      case 'appChangelogEntryDateTime':
        const dates = [
          '2024-03-15 14:30',
          '2024-02-28 10:15',
          '2024-02-01 09:00',
          '2024-01-15 16:45',
          '2024-01-01 12:00'
        ];
        defaultVariableText = dates[changelogDateCounter % dates.length];
        changelogDateCounter++;
        break;
      case 'appChangelogEntryContent':
        const contents = [
          'Major Update - Character Customization Overhaul:\n' +
          '• Advanced facial features with over 50 new options\n' +
          '• Dynamic hair physics and new hairstyles\n' +
          '• Expanded wardrobe system with layered clothing\n\n' +
          'Performance Improvements:\n' +
          '• Optimized rendering pipeline for better FPS\n' +
          '• Reduced loading times by up to 40%\n' +
          '• Fixed memory leaks in character editor',

          'Bug Fix Update:\n' +
          '• Fixed crash when loading large save files\n' +
          '• Resolved inventory sorting issues\n' +
          '• Corrected texture mapping on certain armor sets\n\n' +
          'Quality of Life:\n' +
          '• Added quick save shortcut (Ctrl+S)\n' +
          '• Improved UI scaling for 4K displays',

          'Milestone Release - Combat Update:\n' +
          '• Completely revamped combat system\n' +
          '• Added 15 new weapon types\n' +
          '• Introduced combo system with skill trees\n\n' +
          'New Content:\n' +
          '• 3 new dungeons with unique bosses\n' +
          '• Extended main storyline\n' +
          '• Added new faction: The Shadowkeepers',

          'Performance Update:\n' +
          '• Improved GPU utilization\n' +
          '• Reduced RAM usage by 25%\n' +
          '• Faster level loading times\n\n' +
          'Bug Fixes:\n' +
          '• Fixed multiplayer desync issues\n' +
          '• Resolved achievement tracking bugs',

          'Stability Update:\n' +
          '• Enhanced network connectivity\n' +
          '• Fixed random crashes in multiplayer\n' +
          '• Improved save system reliability\n\n' +
          'Minor Changes:\n' +
          '• Updated UI icons\n' +
          '• Added new sound effects\n' +
          '• Tweaked NPC dialogue system'
        ];
        defaultVariableText = contents[changelogContentCounter % contents.length];
        changelogContentCounter++;
        break;
    }
  }
  */

  if (variable === 'appTaskOperationName') {
    switch (UIData.currentLauncherState[0]) {
      case 'isInstallTaskInProgress': defaultVariableText = 'Installing'; break;
      case 'isUpdateTaskInProgress': defaultVariableText = 'Updating'; break;
      case 'isRepairTaskInProgress': defaultVariableText = 'Repairing'; break;
      case 'isUninstallTaskInProgress': defaultVariableText = 'Uninstalling'; break;
      case 'isMoveTaskInProgress': defaultVariableText = 'Moving'; break;
    }
  }
  if (variable === 'appTaskPercentage') {
    switch (UIData.currentLauncherState[0]) {
      case 'isInstallTaskInProgress': defaultVariableText = '41%'; break;
      case 'isUpdateTaskInProgress': defaultVariableText = '41%'; break;
      case 'isRepairTaskInProgress': defaultVariableText = '41%'; break;
    }
  }
  if (variable === 'appTaskPackageSize') {
    switch (UIData.currentLauncherState[0]) {
      case 'isInstallTaskInProgress': defaultVariableText = '300MB'; break;
      case 'isUpdateTaskInProgress': defaultVariableText = '300MB'; break;
    }
  }
  if (variable === 'appTaskDownloadedSize') {
    switch (UIData.currentLauncherState[0]) {
      case 'isInstallTaskInProgress': defaultVariableText = '124MB'; break;
      case 'isUpdateTaskInProgress': defaultVariableText = '124MB'; break;
    }
  }
  if (variable === 'appTaskDownloadSpeed') {
    switch (UIData.currentLauncherState[0]) {
      case 'isInstallTaskInProgress': defaultVariableText = '59.04 MB/s'; break;
      case 'isUpdateTaskInProgress': defaultVariableText = '59.04 MB/s'; break;
    }
  }
  if (variable === 'appInstallPackageSize') {
    if (UIData.currentLauncherState[0] === 'isAppInstalled' && UIData.currentLauncherState[1] === "false") {
      defaultVariableText = '324MB';
    }
  }
  if (variable === 'appUpdatePackageSize') {
    if (UIData.currentLauncherState[0] === 'isAppUpdateAvailable') {
      defaultVariableText = '213MB';
    }
  }

  const popupOpen: string | undefined = useMemo(() => {
    if (type === 'popup') {
      if (UIData.openPopupIDs.includes(data.id)) return 'true';
    }
    return undefined;
  }, [UIData.openPopupIDs]);

  const renderElement = (HTMLTagName: string) => {
    switch (HTMLTagName) {
      case 'iframe':
        return UIData.editorMode === 'simulation' ? (
          <iframe
            {...launcherStatesProps}
            className={elementCssClassName}
            launcher-element-type={type}
            launcher-element-id={data.id}
            id={data.id}
            src={props?.src}
            allowFullScreen={props?.allowFullScreen}
          />
        ) : (
          <div
            {...launcherStatesProps}
            className={elementCssClassName}
            launcher-element-type={type}
            launcher-element-id={data.id}
            id={data.id}
            style={{
              backgroundColor: '#111',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',

              overflow: 'hidden',
              cursor: 'default',
              transition: UIData.editorMode === 'editing' ? 'none' : undefined,
            }}
          >
            <i
              className="icon-iframe"
              style={{ fontSize: '50px', pointerEvents: 'none', opacity: 0.1 }}
            />
            <span
              style={{
                pointerEvents: 'none',
                fontSize: '14px',
                overflowWrap: 'break-word',
                inlineSize: '100%',
                textAlign: 'center',
                textShadow: '1px 1px 2px #000000',
              }}
            >
              {name}
            </span>
          </div>
        );
      case 'img':
        return (
          <img
            {...launcherStatesProps}
            className={elementCssClassName}
            launcher-element-type={type}
            launcher-element-id={data.id}
            id={data.id}
            src={props?.src}
            draggable={false}
            style={{
              cursor: UIData.editorMode === 'editing' ? 'default' : undefined,
              transition: UIData.editorMode === 'editing' ? 'none' : undefined,
            }}
          />
        );
      case 'span':
        return <span
          ref={elementRef}
          {...launcherStatesProps}
          className={elementCssClassName}
          launcher-element-type={type}
          launcher-element-id={data.id}
          id={data.id}
          style={{
            cursor: UIData.editorMode === 'editing' ? 'default' : undefined,
            opacity: UIData.elementInEditingModeID === data.id ? 0 : undefined,
            transition: UIData.editorMode === 'editing' ? 'none' : undefined,
          }}
        >
          {type === 'variableText' ? defaultVariableText : props?.text}
        </span>;
      case 'a':
        return (
          <a
            {...launcherStatesProps}
            className={elementCssClassName}
            launcher-element-type={type}
            launcher-element-id={data.id}
            id={data.id}
            href={props?.href}
            onClick={(e) => {
              e.preventDefault();
              if (UIData.editorMode === 'simulation') {
                window.open(props?.href, '_blank');
              }
            }}
            style={{
              cursor: UIData.editorMode === 'editing' ? 'default' : undefined,
              transition: UIData.editorMode === 'editing' ? 'none' : undefined,
            }}
          >
            {props?.text}
          </a>
        );
      case 'div':
        return (
          <div
            {...launcherStatesProps}
            className={elementCssClassName}
            launcher-element-type={type}
            popup-open={popupOpen}
            launcher-element-id={data.id}
            id={data.id}
            onClick={() => {
              if (
                (UIData.editorMode === 'simulation') === true &&
                type === 'button'
              ) {
                if (commands) {
                  commands.reduce((promise, command) => {
                    return promise.then(() => {
                      scene.executeCommand(command);
                      return new Promise(resolve => setTimeout(resolve, 100));
                    });
                  }, Promise.resolve());
                }
              }
            }}
            style={{
              cursor: UIData.editorMode === 'editing' ? 'default' : undefined,
              transition: UIData.editorMode === 'editing' ? 'none' : undefined,
            }}
          >
            {/* TODO: add back appChangelog functionality
            {type === 'appChangelog' && UIData.editorMode === 'simulation' ? (
              // Render 5 copies of children for changelog in simulation mode
              Array.from({ length: 5 }).flatMap((_, index) =>
                data?.children?.map((child) => (
                  <LauncherElement key={`${child.id}_${index}`} data={child} />
                ))
              )
            ) : (
              // Normal rendering for other cases
              data?.children?.map((child) => (
                <LauncherElement key={child.id} data={child} />
              ))
            )}
            */}
            {/* Normal rendering for all cases */}
            {data?.children?.map((child) => (
              <LauncherElement key={child.id} data={child} />
            ))}
          </div>
        );
      case 'input':
        return (
          <input
            {...launcherStatesProps}
            className={elementCssClassName}
            launcher-element-type={type}
            launcher-element-id={data.id}
            id={data.id}
            onFocus={(e) => {
              if (UIData.editorMode === 'editing') {
                (e.target as HTMLInputElement).blur();
              }
            }}
            style={{
              cursor: UIData.editorMode === 'editing' ? 'default' : undefined,
              transition: UIData.editorMode === 'editing' ? 'none' : undefined,
            }}
            spellCheck={false}
          />
        );
    }

    return null;
  };

  return renderElement(HTMLTagName);
};
